import React, { useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import RatingButtons from './RatingButtons';
import SessionSummary from './SessionSummary';
import DomainBadge from '../../../shared/components/DomainBadge';
import useKeyboard from '../../../shared/hooks/useKeyboard';
import { renderMarkdown } from '../../../shared/utils/markdownLite';
import { calculateNext, isCorrectRating } from '../engine/sm2';
import { getDueCards, getCramCards, getWeakCards, buildDailyDeck, DAILY_DECK_SIZE, GRADUATION_THRESHOLD } from '../engine/scheduler';

/**
 * Core flashcard review interface.
 *
 * Default review mode uses the Daily Deck system:
 *   - 100 active cards at a time
 *   - Cards graduate after 2 correct rounds in this session
 *   - Graduated cards rotate out; new cards rotate in from waiting pool
 *   - Session ends when all cards are mastered
 *
 * Cram and Weak modes work the same as before (no rotation).
 */
export default function CardReview({ cards, setCards, mode = 'review', cramDomain = null }) {
  const navigate = useNavigate();

  // --- Daily deck state (review mode only) ---
  const [activeIds, setActiveIds] = useState([]);
  const [waitingIds, setWaitingIds] = useState([]);
  const [sessionHits, setSessionHits] = useState({}); // cardId → correct count this session
  const [graduatedCount, setGraduatedCount] = useState(0);
  const [totalServed, setTotalServed] = useState(0); // total unique cards served

  // --- Legacy queue state (cram / weak modes) ---
  const [queue, setQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // --- Shared state ---
  const [flipped, setFlipped] = useState(false);
  const [sessionReviews, setSessionReviews] = useState([]);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [flipKey, setFlipKey] = useState(0); // forces re-mount on card change
  const sessionStartRef = useRef(Date.now());

  // Build the review queue / daily deck based on mode
  useMemo(() => {
    if (mode === 'review') {
      // Daily Deck mode
      const { active, waiting } = buildDailyDeck(cards);
      setActiveIds(active);
      setWaitingIds(waiting);
      setSessionHits({});
      setGraduatedCount(0);
      setTotalServed(active.length);
      setCurrentIndex(0);
    } else {
      // Legacy modes
      let q = [];
      switch (mode) {
        case 'cram':
          q = getCramCards(cards, cramDomain);
          break;
        case 'weak':
          q = getWeakCards(cards);
          break;
        default:
          q = getDueCards(cards);
          break;
      }
      setQueue(q);
      setCurrentIndex(0);
    }
    setFlipped(false);
    setSessionReviews([]);
    setSessionComplete(false);
    sessionStartRef.current = Date.now();
  }, [cards, mode, cramDomain]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Resolve current card ---
  const isDailyDeck = mode === 'review';

  const currentCard = useMemo(() => {
    if (isDailyDeck) {
      if (activeIds.length === 0) return null;
      const idx = currentIndex % activeIds.length;
      const cardId = activeIds[idx];
      return cards.find(c => c.id === cardId) || null;
    }
    return queue[currentIndex] || null;
  }, [isDailyDeck, activeIds, queue, currentIndex, cards]);

  // --- Handle flip ---
  const handleFlip = useCallback(() => {
    if (!flipped && currentCard) {
      setFlipped(true);
    }
  }, [flipped, currentCard]);

  // --- Handle rating ---
  const handleRate = useCallback((rating) => {
    if (!currentCard) return;

    // Update the card's SR data and stats
    const newSrData = mode === 'cram'
      ? currentCard.sr_data
      : calculateNext(currentCard.sr_data, rating);

    const isCorrect = isCorrectRating(rating);
    const updatedCard = {
      ...currentCard,
      sr_data: newSrData,
      stats: {
        times_seen: currentCard.stats.times_seen + 1,
        times_correct: currentCard.stats.times_correct + (isCorrect ? 1 : 0),
        streak: isCorrect ? currentCard.stats.streak + 1 : 0,
      },
    };

    // Persist card update
    setCards(prev => prev.map(c => c.id === updatedCard.id ? updatedCard : c));

    // Track review for session summary
    setSessionReviews(prev => [...prev, {
      cardId: currentCard.id,
      rating,
      domain: currentCard.domain,
    }]);

    if (isDailyDeck) {
      // --- Daily Deck rotation logic ---
      const cardId = currentCard.id;
      const newHits = { ...sessionHits };

      if (isCorrect) {
        newHits[cardId] = (newHits[cardId] || 0) + 1;
      } else {
        // Reset progress on incorrect — must get 2 correct in a row
        newHits[cardId] = 0;
      }
      setSessionHits(newHits);

      if (newHits[cardId] >= GRADUATION_THRESHOLD) {
        // Card graduated — remove from active, pull from waiting
        const newActive = activeIds.filter(id => id !== cardId);
        const newWaiting = [...waitingIds];
        let newTotalServed = totalServed;

        if (newWaiting.length > 0) {
          const nextId = newWaiting.shift();
          newActive.push(nextId);
          newTotalServed++;
        }

        setActiveIds(newActive);
        setWaitingIds(newWaiting);
        setGraduatedCount(prev => prev + 1);
        setTotalServed(newTotalServed);

        if (newActive.length === 0) {
          // All cards mastered!
          setSessionComplete(true);
          return;
        }

        // Adjust index to stay in bounds
        const nextIdx = currentIndex >= newActive.length ? 0 : currentIndex;
        setCurrentIndex(nextIdx);
      } else {
        // Move to next card in the active deck (wrap around)
        setCurrentIndex((currentIndex + 1) % activeIds.length);
      }
      setFlipped(false);
      setFlipKey(k => k + 1);
    } else {
      // --- Legacy mode: linear progression ---
      if (currentIndex + 1 < queue.length) {
        setCurrentIndex(prev => prev + 1);
        setFlipped(false);
        setFlipKey(k => k + 1);
      } else {
        setSessionComplete(true);
      }
    }
  }, [currentCard, currentIndex, mode, setCards, isDailyDeck, activeIds, waitingIds, sessionHits, totalServed, queue.length]);

  // --- Keyboard shortcuts ---
  const keyMap = useMemo(() => {
    if (sessionComplete) return {};
    if (!flipped) {
      return {
        ' ': handleFlip,
        'Enter': handleFlip,
      };
    }
    return {
      '1': () => handleRate(0),
      '2': () => handleRate(1),
      '3': () => handleRate(2),
      '4': () => handleRate(3),
    };
  }, [flipped, sessionComplete, handleFlip, handleRate]);

  useKeyboard(keyMap, !sessionComplete);

  // --- Handle done ---
  const handleDone = useCallback(() => {
    navigate('/flashcards');
  }, [navigate]);

  // --- Empty state ---
  const totalCards = isDailyDeck ? activeIds.length : queue.length;
  if (totalCards === 0 && !sessionComplete) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 page-enter">
        <div className="text-6xl mb-4 animate-scale-in">
          {mode === 'weak' ? '\u{1F4AA}' : '\u{1F389}'}
        </div>
        <h2 className="text-xl font-bold text-white mb-2">
          {mode === 'weak' ? 'No weak cards found!' : 'No cards due!'}
        </h2>
        <p className="text-gray-400 mb-6 text-center">
          {mode === 'weak'
            ? 'All your cards have accuracy above 50%. Keep it up!'
            : 'You\'re all caught up. Come back tomorrow!'}
        </p>
        <button
          onClick={() => navigate('/flashcards')}
          className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl
            hover:from-blue-500 hover:to-blue-400 glow-blue transition-all duration-200"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  // --- Session complete ---
  if (sessionComplete) {
    return (
      <SessionSummary
        reviews={sessionReviews}
        duration={Math.floor((Date.now() - sessionStartRef.current) / 1000)}
        mode={mode}
        onDone={handleDone}
        graduatedCount={graduatedCount}
        totalServed={totalServed}
      />
    );
  }

  // --- Progress info ---
  const progressLabel = isDailyDeck
    ? `${graduatedCount} mastered \u00B7 ${activeIds.length} active \u00B7 ${waitingIds.length} waiting`
    : `Card ${currentIndex + 1} of ${queue.length}`;

  const progressPct = isDailyDeck
    ? totalServed > 0 ? (graduatedCount / totalServed) * 100 : 0
    : (currentIndex / queue.length) * 100;

  const hitsOnCurrent = isDailyDeck && currentCard ? (sessionHits[currentCard.id] || 0) : 0;

  // --- Active review ---
  return (
    <div className="flex flex-col items-center min-h-[80vh] px-4 py-6 page-enter">
      {/* Progress bar */}
      <div className="w-full max-w-2xl mb-6">
        <div className="flex justify-between text-sm text-gray-400 mb-1.5">
          <span>{progressLabel}</span>
          <span className="text-green-400 font-medium">
            {sessionReviews.filter(r => r.rating >= 2).length} correct
          </span>
        </div>
        <div className="w-full bg-gray-700/50 rounded-full h-2 overflow-hidden">
          <div
            className="progress-bar-glow h-2 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${Math.max(progressPct, 1)}%` }}
          />
        </div>
      </div>

      {/* Card */}
      <div
        className="w-full max-w-2xl cursor-pointer"
        key={flipKey}
        onClick={!flipped ? handleFlip : undefined}
      >
        <div className={`card-gradient min-h-[300px] card-flip-reveal ${flipped ? 'revealed' : ''}`}>
          {/* Card header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-700/50">
            <DomainBadge domainId={currentCard.domain} size="xs" />
            <div className="flex items-center gap-3">
              {currentCard.subdomain && (
                <span className="text-xs text-gray-500">{currentCard.subdomain}</span>
              )}
              {isDailyDeck && (
                <span className="text-xs text-gray-500 font-mono">
                  {hitsOnCurrent}/{GRADUATION_THRESHOLD}
                </span>
              )}
              {currentCard.stats.streak > 2 && (
                <span className="text-xs text-yellow-500">{'\u{1F525}'} {currentCard.stats.streak}</span>
              )}
            </div>
          </div>

          {/* Question */}
          <div className="p-8">
            <div className="text-lg text-white leading-relaxed">
              {renderMarkdown(currentCard.front)}
            </div>

            {/* Answer (revealed on flip) */}
            {flipped && (
              <div className="animate-fade-in-up">
                <div className="border-t border-gray-600/50 my-5" />
                <div className="text-lg text-green-400 leading-relaxed">
                  {renderMarkdown(currentCard.back)}
                </div>
                {currentCard.notes && (
                  <div className="mt-4 text-sm text-gray-500 italic glass rounded-lg px-3 py-2">
                    {'\u{1F4DD}'} {currentCard.notes}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Flip prompt */}
          {!flipped && (
            <div className="text-center py-3 text-gray-500 text-sm border-t border-gray-700/50 animate-pulse-soft">
              Tap or press Space to reveal
            </div>
          )}
        </div>
      </div>

      {/* Rating buttons (visible only when flipped) */}
      <div className="w-full max-w-2xl mt-5">
        {flipped ? (
          <div className="animate-fade-in-up">
            <RatingButtons onRate={handleRate} />
          </div>
        ) : (
          <div className="h-[88px]" />
        )}
      </div>

      {/* Done button */}
      <button
        onClick={() => setSessionComplete(true)}
        className="mt-4 text-gray-500 hover:text-gray-300 text-sm transition-colors"
      >
        Done for now ({isDailyDeck
          ? `${activeIds.length + waitingIds.length} remaining`
          : `${queue.length - currentIndex - 1} remaining`})
      </button>
    </div>
  );
}
